import type { GameLaunchCoordinator } from '../game-launch/GameLaunchCoordinator';
import type { LaunchContinuationOwner } from '../_types/LaunchContinuation';
import type { PavilionCoordinator } from '../pavilion/PavilionCoordinator';
import { IDLE_MACRO_RUN_STATE, type StatefulMacroRunState } from './stores';
import type { NativeMacroLaunchManager } from './managers/NativeMacroLaunchManager';
import type { MacroSessionCoordinator } from './MacroSessionCoordinator';

export type LaunchPackageSessions = Pick<MacroSessionCoordinator, 'start' | 'cancel' | 'subscribe'>;
export interface StatefulMacroCoordinatorDeps {
  gameLaunchCoordinator: GameLaunchCoordinator;
  pavilionCoordinator: PavilionCoordinator;
  nativeLaunch?: NativeMacroLaunchManager;
  packageSessions?: LaunchPackageSessions;
}

/** Persisted automatic campaign bindings require explicit graph migration. */
export class StatefulMacroCoordinator {
  private initialized = false;
  private readonly disposers: Array<() => void> = [];
  public constructor(private readonly dependencies: StatefulMacroCoordinatorDeps) {}
  public init(): void {
    if (this.initialized) return;
    this.initialized = true;
    this.disposers.push(this.dependencies.gameLaunchCoordinator.registerMacroStopper(async () => true),
      this.dependencies.gameLaunchCoordinator.events.macroLaunchReady.addListener(() => this.rejectLegacyBinding()));
  }
  public get state(): StatefulMacroRunState { return IDLE_MACRO_RUN_STATE; }
  public subscribeActive(listener: (active: boolean) => void): () => void { listener(false); return () => undefined; }
  public registerDisposer(dispose: () => void): void { this.disposers.push(dispose); }
  public invalidateActiveWork(_reason: string, _nextOwner: LaunchContinuationOwner): void {}
  public destroy(): void { for (const dispose of this.disposers.splice(0)) dispose(); this.initialized = false; }
  private rejectLegacyBinding(): void {
    if (!this.dependencies.gameLaunchCoordinator.isLaunchEventAdmissible()) return;
    const config = this.dependencies.pavilionCoordinator.getCampaignConfig();
    if (config?.useStatefulMacroSystem) this.dependencies.gameLaunchCoordinator.reportCampaignSwitchFailure(
      config.campaignId, 'macro_graph_migration_required', false);
  }
}
